import { YardTaskStatus } from '@nova/core';

import type { Maybe, SpotReserveEntityEnum, SpotStatus, SpotType } from '@nova/core';

import type {
  YardSpotDetails,
  YardSpotAssignmentDetails
} from '@satellite/composables/useYardSpotPresenter';

/**
 * Active MOVE yard task hint from yard-view BFF (`YardViewOpenMoveTaskRefDto`: spot grid, list rows, spot detail).
 * `role`: origin = task tied to assigned container on this spot; destination = task whose
 * `destinationSpotId` is this spot.
 */
export type SpotGridOpenMoveTaskRef = {
  id: string;
  refNum?: Maybe<string>;
  role?: 'origin' | 'destination';
  status?: YardTaskStatus;
  child?: {
    originSpot?: { id: string };
    destinationSpot?: { id: string };
    assetContainer?: { id: string };
  };
};

/**
 * Active CHECK Asset yard task hint from yard-view BFF (`YardViewOpenCheckTaskRefDto`).
 */
export type SpotGridOpenCheckTaskRef = {
  id: string;
  refNum?: Maybe<string>;
};

/**
 * One `SpotAssignment` row from spot-grid JSON: same relations as the Neutron entity graph
 * (`assetVisit` / `assetContainer` nested), without server-side-only assignment timestamps.
 */
export type SpotGridAssignment = Pick<
  YardSpotAssignmentDetails,
  'id' | 'spotId' | 'assetVisitId' | 'assetContainerId' | 'assetVisit' | 'assetContainer'
> & {
  observation?: Maybe<string>;
  /** ISO-8601 from BFF (see `SpotGridAssignmentDto`) */
  createDateTime: string;
  lastChangedDateTime: string;
};

export type SpotGridReserveRef = {
  id: string;
  isActive: boolean;
  entityName: SpotReserveEntityEnum;
  objectId: string;
  validUntil?: Maybe<string>;
  /** ISO-8601 from BFF (see `YardViewSpotReserveRefDto`) */
  createDateTime: string;
  lastChangedDateTime: string;
};

/**
 * One row from `GET /yard/view/spot-grid`: {@link YardSpotDetails} fields (partial where BFF omits)
 * plus BFF-only `openMoveTasks` / `openCheckTasks` and spot audit timestamps (see Neutron `SpotGridItemDto`).
 * Spot lists use BFF JSON shapes (`SpotGridAssignment`, `SpotGridReserveRef`) with ISO-8601 strings.
 */
export type SpotGridItem = {
  id: string;
  code: string;
  type: SpotType;
  isActive: boolean;
  /** First-class spot status from the yard-view BFF (`SpotGridItemDto`) */
  status: SpotStatus;
  spotAreaId: string;
  /** ISO-8601 from BFF (`SpotGridItemDto`) */
  createDateTime: string;
  /** ISO-8601 from BFF (`SpotGridItemDto`) */
  lastChangedDateTime: string;
  spotAssignments?: SpotGridAssignment[];
  spotReserves?: SpotGridReserveRef[];
  openMoveTasks?: SpotGridOpenMoveTaskRef[];
  openCheckTasks?: SpotGridOpenCheckTaskRef[];
};

/**
 * One area from `GET /yard/view/spot-grid` (`SpotGridAreaDto`).
 * Keep this shape explicit to decouple spot-grid DTO typing from `YardAreaDetails`.
 */
export type SpotGridArea = {
  id: string;
  name: string;
  warehouseId: string;
  orgId: string;
  spots: SpotGridItem[];
};

/**
 * A destination can have more than one open move targeting it — the loser of a reserve race stays
 * `created` and blocked, but still carries a `destination` role here. Only `in-progress` is
 * actually moving, so it wins when present; otherwise the oldest (array order, unchanged).
 */
export function getInboundMoveTask(spot: SpotGridItem): SpotGridOpenMoveTaskRef | null {
  const destinationTasks = (spot.openMoveTasks ?? []).filter(task => task.role === 'destination');

  return (
    destinationTasks.find(task => task.status === YardTaskStatus.IN_PROGRESS) ??
    destinationTasks[0] ??
    null
  );
}

export function getInboundAssetContainerId(spot: SpotGridItem): string | null {
  return getInboundMoveTask(spot)?.child?.assetContainer?.id ?? null;
}

/**
 * The spot's inbound move, only when it is actually moving. Unlike {@link getInboundMoveTask},
 * this never falls back to a still-`created` destination task — a ghost only makes sense for an
 * asset physically between spots.
 */
export function getInboundInProgressMoveTask(spot: SpotGridItem): SpotGridOpenMoveTaskRef | null {
  const destinationTask = getInboundMoveTask(spot);

  return destinationTask?.status === YardTaskStatus.IN_PROGRESS ? destinationTask : null;
}
